import React, { useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Alert,
    CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import LockResetIcon from "@mui/icons-material/LockReset";
import axios from "axios";
import { useParams } from "react-router-dom";

const StyledPaper = styled(Paper)(({ theme, mode, customColors }) => ({
    padding: theme.spacing(5),
    maxWidth: 420,
    margin: "40px auto",
    borderRadius: "16px",
    boxShadow: mode === "dark"
        ? "0 8px 32px rgba(0, 0, 0, 0.4)"
        : "0 8px 32px rgba(30, 58, 138, 0.12)",
}));

export default function CreatePasswordPage({ mode, customColors }) {
    const { uid, token } = useParams();   // Gets uidb64 and token from URL

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");

    const apiUrl = process.env.REACT_APP_BASE_URL;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess(false);

        if (password !== confirmPassword) {
            setError("Passwords do not match!");
            return;
        }

        if (password.length < 6) {
            setError("Password must be at least 6 characters long.");
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(
                `${apiUrl}/set-new-password/${uid}/${token}/`,
                {
                    password: password,
                    confirm_password: confirmPassword,  
                }
            );

            setSuccess(true);
            setPassword("");
            setConfirmPassword("");

            setTimeout(() => {
                window.location.href = "/";   // Redirect to login page
            }, 2000);

        } catch (err) {
            console.error("Password reset error:", err);
            setError(
                err.response?.data?.detail ||
                err.response?.data?.message ||
                err.response?.data?.error ||
                "Failed to set password. The link may be expired or invalid."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: mode === "dark"
                    ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                    : "linear-gradient(135deg, #f0f7ff 0%, #e0f0ff 100%)",
            }}
        >
            <StyledPaper mode={mode} customColors={customColors}>
                <Box sx={{ textAlign: "center", mb: 4 }}>
                    <LockResetIcon sx={{ fontSize: 60, color: "#1e8bafff", mb: 2 }} />
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                        }}
                    >
                        Set New Password
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Create a secure password for your account
                    </Typography>
                </Box>

                {success && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        Password set successfully! Redirecting to login...
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                        label="New Password"
                        type="password"
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        sx={{ mb: 3 }}
                        disabled={success || loading}
                    />

                    <TextField
                        label="Confirm New Password"
                        type="password"
                        fullWidth
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        sx={{ mb: 4 }}
                        disabled={success || loading}
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={loading || success || !password || !confirmPassword}
                        sx={{
                            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                            py: 1.5,
                            fontSize: "1.05rem",
                            textTransform: "none",
                            borderRadius: "12px",
                            boxShadow: "0 4px 14px rgba(30, 139, 175, 0.3)",
                            "&:hover": {
                                background: "linear-gradient(135deg, #1a3278 0%, #1a7a9a 100%)",
                            },
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : "Set Password"}
                    </Button>
                </Box>

                <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 3, color: "text.secondary" }}>
                    After setting the password, you will be redirected to the login page.
                </Typography>
            </StyledPaper>
        </Box>
    );
}